import { FormikErrors, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import VoxelAutocompleteCity from '../../../components/muiLegacy/Autocomplete/VoxelAutocompleteCity';
import VoxelAutocompleteCountry, {
  globalOption,
} from '../../../components/muiLegacy/Autocomplete/VoxelAutocompleteCountry';
import VoxelLink from '../../../components/voxel/Link/VoxelLink';
import VoxelFormPanel from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanel';
import { GoogleAdsLocationGq } from '../../../graphql/projects/models/GoogleAdsLocationGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { useProjectDefaults } from '../../../logic/otherUtilities/useProjectDefaults';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faUnlock } from '@fortawesome/pro-regular-svg-icons';
import VoxelPanelActions from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelPanelActions';
import VoxelFormPanelActionSubmit from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanelActionSubmit';
import VoxelFormPanelActionCancel from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanelActionCancel';
import VoxelAlertDialog from '../../../components/voxel/Dialogs/VoxelAlertDialog';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelDialogActions from '../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import LockIcon from '../../../components/voxel/Icons/LockIcon';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActionCancel from '../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelFormPanelContent from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanelContent';

export interface FormState {
  country: GoogleAdsLocationGq;
  city: GoogleAdsLocationGq;
  rankCountry: GoogleAdsLocationGq;
  rankCity: GoogleAdsLocationGq;
}

export interface LocationFormProps {
  project: ProjectGq;
  setDirty: (dirty: boolean) => void;
  setSave: (saveFunction: () => Promise<unknown>) => void;
  reload: () => Promise<unknown>;
  disabled?: boolean;
}

const LocationForm: React.FC<LocationFormProps> = ({
  project,
  setDirty,
  setSave,
  reload,
  disabled,
}) => {
  // Initial form values
  const projectDefaults = useProjectDefaults(project);

  const defaultIsGlobal = !projectDefaults.volumeCountry;
  const serpLocationRequired = defaultIsGlobal && !projectDefaults.serpCountry;
  const formIsDefaultLocked =
    (project.projectHasKeywordRequests || project.progressHasSerp) && !serpLocationRequired;

  const [formLocked, setFormLocked] = useState(formIsDefaultLocked);
  const [openUnlockWarning, setOpenUnlockWarning] = useState(false);

  const mutation = projectsApiService.mutations.projects.useUpdateProjectLocation();
  const formikProps = useFormik<FormState>({
    initialValues: {
      country: projectDefaults.volumeCountry ?? globalOption,
      city: projectDefaults.volumeCity,
      rankCountry: projectDefaults.serpCountry,
      rankCity: projectDefaults.serpCity,
    },
    validate: (data) => {
      const errors: FormikErrors<FormState> = {};
      return errors;
    },
    enableReinitialize: true,
    onSubmit: (values, actions) =>
      mutation({
        projectId: project.id,
        locationId: !!values.city?.id
          ? values.city?.id
          : values.country?.id === globalOption.id
            ? null
            : values.country?.id,
        locationName: !!values.city?.id
          ? values.city?.name
          : values.country?.id === globalOption.id
            ? null
            : values.country.name,
        rankLocationId:
          values.country?.id !== globalOption.id
            ? !!values.city?.id
              ? values.city?.id
              : values.country?.id
            : !!values.rankCity?.id
              ? values.rankCity?.id
              : values.rankCountry?.id,
        rankLocationName:
          values.country?.id !== globalOption.id
            ? !!values.city?.id
              ? values.city?.name
              : values.country?.name
            : !!values.rankCity?.id
              ? values.rankCity?.name
              : values.rankCountry?.name,
      })
        .catch((error) => console.error(error))
        .then(() => setFormLocked(true))
        .then(() => reload()),
  });

  useEffect(() => setSave(formikProps.submitForm), [formikProps.submitForm]);
  useEffect(() => {
    setDirty(formikProps.dirty);
    return () => setDirty(false);
  }, [formikProps.dirty]);

  return (
    <VoxelFormPanel
      disabled={disabled}
      title={
        <span className={'flex flex-row items-center gap-2'}>
          {!formLocked ? null : <LockIcon />}
          Location
        </span>
      }
      subtitle={
        <span>
          The location settings are based the list of{' '}
          <VoxelLink
            openInNewIcon
            openInNewTab
            href="https://developers.google.com/google-ads/api/data/geotargets"
          >
            geo targets
          </VoxelLink>{' '}
          made availabe by Google Ads.{' '}
          <VoxelLink
            openInNewTab
            href="https://support.google.com/google-ads/answer/1722043?hl=en&ref_topic=3119074&sjid=8646136592463639870-EU"
          >
            Read more
          </VoxelLink>
          at Google.
        </span>
      }
    >
      <VoxelFormPanelContent disabled={disabled}>
        <div className="w-full flex flex-row gap-4">
          <VoxelAutocompleteCountry
            type="both"
            labelPosition="top"
            selectedCountry={formikProps.values.country}
            setSelectedCountry={(country) => formikProps.setFieldValue('country', country)}
            clearSelectedCity={() => formikProps.setFieldValue('city', null)}
            disabled={formLocked || formikProps.isSubmitting}
            inputWidthClass="w-full"
          />
          {formikProps.values.country.id !== globalOption.id && (
            <VoxelAutocompleteCity
              type="both"
              labelPosition="top"
              selectedCountry={formikProps.values.country}
              selectedCity={formikProps.values.city}
              setSelectedCity={(city) => formikProps.setFieldValue('city', city)}
              disabled={
                formLocked ||
                formikProps.isSubmitting ||
                formikProps.values.country.id === globalOption.id
              }
              inputWidthClass="w-full"
            />
          )}
          {formikProps.values.country.id === globalOption.id && (
            <VoxelAutocompleteCountry
              type="rank"
              labelPosition="top"
              selectedCountry={formikProps.values.rankCountry}
              setSelectedCountry={(country) => formikProps.setFieldValue('rankCountry', country)}
              clearSelectedCity={() => formikProps.setFieldValue('rankCity', null)}
              helperText={!formikProps.values.rankCountry ? 'Required' : null}
              showHelperText={!formikProps.values.rankCountry}
              disabled={formLocked || formikProps.isSubmitting}
              inputWidthClass="w-full"
            />
          )}
          {formikProps.values.country.id === globalOption.id && (
            <VoxelAutocompleteCity
              type="rank"
              labelPosition="top"
              selectedCountry={formikProps.values.rankCountry}
              selectedCity={formikProps.values.rankCity}
              setSelectedCity={(city) => formikProps.setFieldValue('rankCity', city)}
              disabled={formLocked || formikProps.isSubmitting || !formikProps.values.rankCountry}
              inputWidthClass="w-full"
            />
          )}
        </div>
      </VoxelFormPanelContent>
      <VoxelPanelActions>
        {formLocked && (
          <VoxelFormPanelActionSubmit
            color="gray"
            onClick={() => setOpenUnlockWarning(true)}
            title={'Unlock to edit'}
            endIcon={<FontAwesomeIcon icon={faUnlock} />}
            disabled={disabled}
          />
        )}
        {!formLocked && formIsDefaultLocked && (
          <VoxelFormPanelActionCancel onClick={() => setFormLocked(true)} />
        )}
        {!formLocked && (
          <VoxelFormPanelActionSubmit
            title={'Save changes'}
            onClick={formikProps.submitForm}
            disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
            endIcon={<FontAwesomeIcon icon={faSave} />}
          />
        )}
      </VoxelPanelActions>
      <VoxelAlertDialog
        title="Data loss"
        severity="warning"
        open={openUnlockWarning}
        onClose={() => setOpenUnlockWarning(false)}
      >
        <VoxelDialogContent>
          <VoxelText>
            <span>
              Editing will trigger a refetch of any keyword or rank data that is not consistent with
              the new setting. If the refetch should turn out to be unsuccessfull the data will be
              deleted instead.{' '}
            </span>
            <br></br>
            <br></br>
            This is to ensure data consistency.
          </VoxelText>
        </VoxelDialogContent>
        <VoxelDialogActions>
          <VoxelDialogActionCancel onClick={() => setOpenUnlockWarning(false)} />
          <VoxelDialogActionSubmit
            color="gray"
            onClick={() => {
              setFormLocked(false);
              setOpenUnlockWarning(false);
            }}
            title={'Proceed'}
          />
        </VoxelDialogActions>
      </VoxelAlertDialog>
    </VoxelFormPanel>
  );
};

export default LocationForm;
